import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import dataService from '../services/dataService';
import { useAuth } from '../context/AuthContext';
import { UserIcon, BuildingOfficeIcon, PlusIcon, XMarkIcon, MagnifyingGlassIcon, LinkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const Visitors = () => {
    const [visitors, setVisitors] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Add Visitor Modal State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [visitorForm, setVisitorForm] = useState({
        name: '',
        email: '',
        contact_number: '',
        company_name: '',
        chapter: '',
        registration_type: '',
        registered_for: '',
        meeting_id: '',
        event_id: '',
        training_id: '',
        payment_confirmed: false
    });

    const { user } = useAuth();
    const chapterLabel = user?.namingConvention?.chapterLabel || 'Chapter';
    const meetingLabel = user?.namingConvention?.meetingLabel || 'Meeting';
    const isChapterAdmin = user?.role === 'chapter_admin';
    const isAdmin = user?.role === 'admin' || isChapterAdmin;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    // Filter visitors based on search query
    const filteredVisitors = visitors.filter(visitor =>
        (visitor.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (visitor.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (visitor.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (visitor.chapter || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination logic
    const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedVisitors = filteredVisitors.slice(startIndex, endIndex);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    useEffect(() => {
        fetchVisitors();
        fetchChapters();
        fetchMeetings();
        fetchEvents();
        fetchTrainings();
    }, []);

    const [upcomingMeetings, setUpcomingMeetings] = useState([]);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [upcomingTrainings, setUpcomingTrainings] = useState([]);

    const fetchMeetings = async () => {
        try {
            const res = await dataService.getChapterMeetings();
            if (res.data && res.data.success) {
                // Filter for upcoming meetings
                const filtered = res.data.data.filter(m => new Date(m.meeting_date) >= new Date().setHours(0, 0, 0, 0));
                setUpcomingMeetings(filtered);
            }
        } catch (error) {
            console.error('Failed to fetch meetings', error);
        }
    };

    const fetchEvents = async () => {
        try {
            const res = await dataService.getEvents();
            if (res.data && res.data.success) {
                // Filter for upcoming events
                const filtered = res.data.data.filter(e => new Date(e.event_date) >= new Date().setHours(0, 0, 0, 0));
                setUpcomingEvents(filtered);
            }
        } catch (error) {
            console.error('Failed to fetch events', error);
        }
    };

    const fetchTrainings = async () => {
        try {
            const res = await dataService.getTrainings();
            if (res.data && res.data.success) {
                // Filter for upcoming trainings
                const filtered = res.data.data.filter(t => new Date(t.training_start_date) >= new Date().setHours(0, 0, 0, 0));
                setUpcomingTrainings(filtered);
            }
        } catch (error) {
            console.error('Failed to fetch trainings', error);
        }
    };

    const fetchChapters = async () => {
        try {
            const res = await dataService.getChapters();
            if (res.data && res.data.success) {
                setChapters(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch chapters', error);
        }
    };

    const fetchVisitors = async () => {
        try {
            const res = await dataService.getVisitors(); // Ensure this exists in dataService
            if (res.data.success) {
                setVisitors(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch visitors', error);
        } finally {
            setLoading(false);
        }
    };

    const getSelectedTargetDetails = () => {
        if (visitorForm.registration_type === 'meeting' && visitorForm.meeting_id) {
            const meeting = upcomingMeetings.find(m => m.id === parseInt(visitorForm.meeting_id));
            if (meeting) {
                return {
                    charges: meeting.charges,
                    payment_link: meeting.payment_link
                };
            }
        } else if (visitorForm.registration_type === 'event' && visitorForm.event_id) {
            const event = upcomingEvents.find(e => e.id === parseInt(visitorForm.event_id));
            if (event) {
                return {
                    charges: event.event_charges,
                    payment_link: event.payment_link
                };
            }
        } else if (visitorForm.registration_type === 'training' && visitorForm.training_id) {
            const training = upcomingTrainings.find(t => t.id === parseInt(visitorForm.training_id));
            if (training) {
                return {
                    charges: training.training_charges,
                    payment_link: training.payment_link
                };
            }
        }
        return null;
    };

    const validateContactNo = (contactNo) => {
        const phone = contactNo.trim();

        // Must contain exactly 10 digits
        if (!/^\d{10}$/.test(phone)) {
            return 'Contact number must be exactly 10 digits.';
        }

        // Do not allow all zeros
        if (/^0{10}$/.test(phone)) {
            return 'Please enter a valid contact number. 0000000000 is not allowed.';
        }

        // // Optional: Indian mobile number should start from 6-9
        // if (!/^[6-9]\d{9}$/.test(phone)) {
        //     return 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.';
        // }

        return '';
    };

    const handleAddVisitor = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
         const contactError = validateContactNo(visitorForm.contact_number);
        
                if (contactError) {
                    toast.error(contactError);
                    return;
                }
        setIsSubmitting(true);
        try {
            await dataService.addVisitor(visitorForm);
            toast.success('Visitor added successfully. Notification email sent.');
            setIsAddModalOpen(false);
            setVisitorForm({
                name: '',
                email: '',
                contact_number: '',
                company_name: '',
                chapter: '',
                registration_type: '',
                registered_for: '',
                meeting_id: '',
                event_id: '',
                training_id: '',
                payment_confirmed: false
            });
            fetchVisitors(); // Refresh the list
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Failed to add visitor');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Visitors</h1>
                    <p className="text-slate-500 font-medium">Manage and track guests attending your {chapterLabel.toLowerCase()} {meetingLabel.toLowerCase()}s.</p>
                </div>
                {isAdmin && (
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-2xl font-bold shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all active:scale-95"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Visitor
                    </button>
                )}
            </div>

            {/* Premium Controls */}
            <div className="bg-white p-6 rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="relative flex-1 max-w-xl">
                    <MagnifyingGlassIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name, email, company or chapter..."
                        className="w-full bg-gray-50 border border-transparent rounded-2xl pl-12 pr-12 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-gray-100 focus:bg-white focus:border-gray-200 transition-all shadow-inner"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="flex gap-4">
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 px-4 py-2 rounded-xl border border-gray-100 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        Total Visitors: {filteredVisitors.length}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden p-6 md:p-8">
                {filteredVisitors.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                        <UserIcon className="w-12 h-12 opacity-20 mb-3" />
                        <p className="text-sm font-bold uppercase tracking-widest opacity-60">No visitors record found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                        {paginatedVisitors.map((visitor) => (
                            <div
                                key={visitor.id || visitor.visitor_id || Math.random()}
                                className="group relative bg-white border-2 border-slate-100 rounded-3xl p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                            >
                                <div>
                                    {/* Header */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center border-2 border-white shadow-md ring-2 ring-slate-50 flex-shrink-0">
                                            <UserIcon className="w-6 h-6 text-primary-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-base font-black text-slate-900 truncate">
                                                {visitor.name}
                                            </h3>
                                            <p className="text-xs font-semibold text-slate-500 truncate" title={visitor.email}>
                                                {visitor.email}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                Company
                                            </p>
                                            <p className="text-xs font-bold text-slate-700 truncate" title={visitor.company_name}>
                                                {visitor.company_name || 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                Contact
                                            </p>
                                            <p className="text-xs font-bold text-slate-700">
                                                {visitor.contact_number || 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-span-2">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                Registered For
                                            </p>
                                            <p className="text-xs font-bold text-primary-600 truncate" title={visitor.registered_for}>
                                                <span className="text-[9px] font-black uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded mr-1.5">{visitor.type || 'Manual'}</span>
                                                {visitor.registered_for || 'General Business'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                Chapter
                                            </p>
                                            <p className="text-xs font-bold text-slate-700 truncate">
                                                {visitor.chapter || 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                Added By
                                            </p>
                                            <p className="text-xs font-bold text-slate-700 truncate">
                                                {visitor.added_by_name || 'Unknown'}
                                            </p>
                                        </div>
                                        <div className="col-span-2">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                Date Added
                                            </p>
                                            <p className="text-xs font-bold text-slate-700">
                                                {new Date(visitor.created_at || visitor.registration_date).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Pagination Controls */}
                <div className="flex flex-col md:flex-row items-center justify-between px-8 py-5 border-t border-slate-100 gap-4 bg-gray-50/50">
                    <div className="text-sm font-semibold text-slate-600">
                        Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredVisitors.length)}</span> of{' '}
                        <span className="font-black text-slate-900">{filteredVisitors.length}</span> {filteredVisitors.length === 1 ? 'visitor' : 'visitors'}
                    </div>

                    {filteredVisitors.length > itemsPerPage && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                    : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                    }`}
                            >
                                Previous
                            </button>

                            <div className="flex items-center gap-1">
                                {[...Array(totalPages)].map((_, index) => {
                                    const pageNum = index + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === totalPages ||
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                    ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                    : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                        return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                    : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Visitor Modal */}
            <Transition.Root show={isAddModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[60]" onClose={() => setIsAddModalOpen(false)}>
                    <Transition.Child as={Fragment}>
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment}>
                                <Dialog.Panel className="w-full max-w-md bg-white rounded-[30px] shadow-2xl p-8">
                                    <div className="flex justify-between items-center mb-6">
                                        <Dialog.Title className="text-xl font-black text-slate-900">Add New Visitor</Dialog.Title>
                                        <button onClick={() => setIsAddModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400">
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>
                                    <form onSubmit={handleAddVisitor} className="space-y-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Visitor Name *</label>
                                            <input required type="text" value={visitorForm.name} onChange={e => setVisitorForm({ ...visitorForm, name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email *</label>
                                            <input required type="email" value={visitorForm.email} onChange={e => setVisitorForm({ ...visitorForm, email: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contact Number *</label>
                                            <input required type="text" value={visitorForm.contact_number} onChange={e => setVisitorForm({ ...visitorForm, contact_number: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Company Name</label>
                                            <input type="text" value={visitorForm.company_name} onChange={e => setVisitorForm({ ...visitorForm, company_name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{chapterLabel} *</label>
                                            <select
                                                required
                                                value={visitorForm.chapter}
                                                onChange={e => setVisitorForm({ ...visitorForm, chapter: e.target.value })}
                                                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value="">Select {chapterLabel}</option>
                                                {chapters.map((ch) => (
                                                    <option key={ch.id} value={ch.name}>{ch.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Registration Type *</label>
                                            <select
                                                required
                                                value={visitorForm.registration_type}
                                                onChange={e => {
                                                    setVisitorForm({
                                                        ...visitorForm,
                                                        registration_type: e.target.value,
                                                        meeting_id: '',
                                                        event_id: '',
                                                        training_id: '',
                                                        registered_for: ''
                                                    });
                                                }}
                                                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value="">Select Registration Type</option>
                                                <option value="meeting">{chapterLabel} {meetingLabel}</option>
                                                <option value="event">Event</option>
                                                <option value="training">Training</option>
                                            </select>
                                        </div>

                                        {visitorForm.registration_type === 'meeting' && (
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Select {chapterLabel} {meetingLabel} *</label>
                                                <select
                                                    required
                                                    value={visitorForm.meeting_id}
                                                    onChange={e => {
                                                        const meeting = upcomingMeetings.find(m => m.id === parseInt(e.target.value));
                                                        setVisitorForm({
                                                            ...visitorForm,
                                                            meeting_id: e.target.value,
                                                            registered_for: meeting ? meeting.title : '',
                                                            chapter: meeting ? meeting.chapter : visitorForm.chapter
                                                        });
                                                    }}
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                >
                                                    <option value="">Select {meetingLabel}</option>
                                                    {upcomingMeetings.map((mtg) => (
                                                        <option key={mtg.id} value={mtg.id}>{mtg.title} ({new Date(mtg.meeting_date).toLocaleDateString('en-GB')})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {visitorForm.registration_type === 'event' && (
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Select Event *</label>
                                                <select
                                                    required
                                                    value={visitorForm.event_id}
                                                    onChange={e => {
                                                        const event = upcomingEvents.find(ev => ev.id === parseInt(e.target.value));
                                                        setVisitorForm({
                                                            ...visitorForm,
                                                            event_id: e.target.value,
                                                            registered_for: event ? event.title : '',
                                                            chapter: event ? event.chapter : visitorForm.chapter
                                                        });
                                                    }}
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                >
                                                    <option value="">Select Event</option>
                                                    {upcomingEvents.map((evt) => (
                                                        <option key={evt.id} value={evt.id}>{evt.title} ({new Date(evt.event_date).toLocaleDateString('en-GB')})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {visitorForm.registration_type === 'training' && (
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Select Training *</label>
                                                <select
                                                    required
                                                    value={visitorForm.training_id}
                                                    onChange={e => {
                                                        const training = upcomingTrainings.find(t => t.id === parseInt(e.target.value));
                                                        setVisitorForm({
                                                            ...visitorForm,
                                                            training_id: e.target.value,
                                                            registered_for: training ? training.training_title : '',
                                                            chapter: training ? training.chapter : visitorForm.chapter
                                                        });
                                                    }}
                                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                                >
                                                    <option value="">Select Training</option>
                                                    {upcomingTrainings.map((trn) => (
                                                        <option key={trn.id} value={trn.id}>{trn.training_title} ({new Date(trn.training_start_date).toLocaleDateString('en-GB')})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {/* Payment Details */}
                                        {(() => {
                                            const details = getSelectedTargetDetails();
                                            if (details && Number(details.charges || 0) > 0) {
                                                return (
                                                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3">
                                                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Payment Options</h4>
                                                        <div className="flex justify-between items-center text-sm font-semibold">
                                                            <span className="text-slate-500">Charges:</span>
                                                            <span className="text-slate-900 font-bold">₹{details.charges}</span>
                                                        </div>
                                                        {details.payment_link && (
                                                            <div className="text-xs font-semibold">
                                                                <span className="text-slate-500 block mb-1">Payment Link:</span>
                                                                <a 
                                                                    href={details.payment_link} 
                                                                    target="_blank" 
                                                                    rel="noreferrer" 
                                                                    className="text-primary-600 hover:underline break-all inline-flex items-center gap-1"
                                                                >
                                                                    <LinkIcon className="w-3.5 h-3.5" />
                                                                    {details.payment_link}
                                                                </a>
                                                            </div>
                                                        )}
                                                        <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-slate-200">
                                                            <input
                                                                type="checkbox"
                                                                checked={visitorForm.payment_confirmed || false}
                                                                onChange={(e) => setVisitorForm({ ...visitorForm, payment_confirmed: e.target.checked })}
                                                                className="w-4.5 h-4.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                                            />
                                                            <span className="text-xs font-bold text-slate-700 select-none">Payment Done</span>
                                                        </label>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                                        >
                                            {isSubmitting ? 'Adding Visitor...' : 'Add Visitor'}
                                        </button>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
};

export default Visitors;
